import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { supabase } from "../utils/supabaseClient";

const ProUpgradeDialog = ({ open, onClose, feature, limit }) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUpgrade = async () => {
    if (window.gtag) {
      window.gtag("event", "go_pro_clicked", {
        feature: feature,
        limit: limit,
      });
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // Not logged in -> trigger signup modal
        setIsLoading(false);
        onClose();
        window.dispatchEvent(new CustomEvent("open-signup"));
        return;
      }

      // Logged in (e.g. Google Auth bypass or free user) -> redirect to Stripe
      const { data: functionData, error: functionError } =
        await supabase.functions.invoke("create-stripe-checkout", {
          body: JSON.stringify({
            email: user.email,
            plan: "annual", // Best deal by default
            theme: theme.palette.mode || "dark",
          }),
        });

      if (functionError || functionData?.error) {
        console.error("Stripe Checkout Error:", functionError || functionData?.error);
        setError("Failed to start checkout. Please contact support.");
        setIsLoading(false);
        return;
      }

      if (functionData?.checkout_url) {
        window.location.href = functionData.checkout_url;
      } else {
        setError("Failed to start checkout (missing URL).");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Unexpected upgrade error:", err);
      setError("An unexpected error occurred.");
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!isLoading ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          backgroundImage:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, #1e1e1e, #121212)"
              : "linear-gradient(145deg, #ffffff, #f5f5f7)",
        },
      }}
    >
      <DialogContent sx={{ textAlign: "center", pt: 4, pb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <img
            src={
              theme.palette.mode === "dark" ? "/pro-dark.svg" : "/pro-light.svg"
            }
            alt="FlyBnB Pro"
            style={{ width: "140px", height: "auto" }}
          />
        </Box>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          {feature === "SavedSearch"
            ? "Saving searches is a Pro perk"
            : "Unlock the rest of FlyBnB"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {feature === "SavedSearch"
            ? "Pro members save searches under their profile and re-run them in one tap."
            : "Sounds like you're actually planning something. Go Pro for unlimited searches, AI tools, and advanced filters."}
        </Typography>
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: "bold" }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: "center", pb: 4, px: 3, gap: 1, flexDirection: "column" }}
      >
        <Button
          onClick={handleUpgrade}
          variant="contained"
          fullWidth
          size="large"
          disabled={isLoading}
          sx={{
            fontWeight: "bold",
            py: 1.5,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Go Pro"}
        </Button>
        <Button
          onClick={onClose}
          variant="text"
          disabled={isLoading}
          sx={{ color: "text.secondary", textTransform: "none" }}
        >
          Maybe later
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProUpgradeDialog;
